import React, { useState, useMemo } from "react";
import { Calendar } from "primereact/calendar";
import { DataTable, DataTableExpandedRows } from "primereact/datatable";
import { Column } from "primereact/column";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { usePurchaseOrderSummary } from "presentation/hooks/use-query-purchase-order";
import * as XLSX from "xlsx";

const PurchaseOrderMonthlySummary = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [expandedRows, setExpandedRows] = useState<
    any[] | DataTableExpandedRows | undefined
  >(undefined);

  const [selectedPOs, setSelectedPOs] = useState<string[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);

  const month = selectedDate.getMonth() + 1;
  const year = selectedDate.getFullYear();

  const { data: summaryData, isLoading: isSummaryLoading } =
    usePurchaseOrderSummary(month, year);

  const uniqueClients = useMemo(() => {
    if (!summaryData?.data) return [];
    const clients = Array.from(
      new Set(
        summaryData.data
          .map((item: any) => item.clientName)
          .filter((name: string) => Boolean(name)),
      ),
    );
    return clients.map((client) => ({
      label: client as string,
      value: client as string,
    }));
  }, [summaryData]);

  const uniquePOs = useMemo(() => {
    if (!summaryData?.data) return [];
    const posMap = new Map();
    summaryData.data.forEach((item: any) => {
      if (!posMap.has(item.poNumber)) {
        posMap.set(item.poNumber, `${item.poName} (${item.poNumber})`);
      }
    });
    return Array.from(posMap.entries()).map(([value, label]) => ({
      label,
      value,
    }));
  }, [summaryData]);

  const uniqueEmployees = useMemo(() => {
    if (!summaryData?.data) return [];
    const employees = Array.from(
      new Set(summaryData.data.map((item: any) => item.employeeName)),
    );
    return employees.map((emp) => ({
      label: emp as string,
      value: emp as string,
    }));
  }, [summaryData]);

  const groupedData = useMemo(() => {
    if (!summaryData?.data) return [];

    const groups: Record<string, any> = {};

    summaryData.data.forEach((item: any) => {
      if (
        selectedClients.length > 0 &&
        !selectedClients.includes(item.clientName)
      )
        return;
      if (selectedPOs.length > 0 && !selectedPOs.includes(item.poNumber))
        return;
      if (
        selectedEmployees.length > 0 &&
        !selectedEmployees.includes(item.employeeName)
      )
        return;

      const key = item.poNumber;
      if (!groups[key]) {
        groups[key] = {
          clientName: item.clientName,
          poName: item.poName,
          poNumber: item.poNumber,
          poType: item.poType,
          totalHours: 0,
          totalEuros: 0,
          employees: [],
        };
      }

      groups[key].totalHours += Number(item.totalHours || 0);
      groups[key].totalEuros += Number(item.totalEuros || 0);

      groups[key].employees.push({
        employeeName: item.employeeName,
        totalHours: Number(item.totalHours || 0),
        totalEuros: Number(item.totalEuros || 0),
      });
    });

    return Object.values(groups);
  }, [summaryData, selectedClients, selectedPOs, selectedEmployees]);

  const rowExpansionTemplate = (data: any) => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          margin: "0 -1rem",
        }}
      >
        <DataTable
          value={data.employees}
          size="small"
          emptyMessage="Nenhum detalhe encontrado."
          style={{ width: "50%", fontSize: "0.85rem" }}
          showHeaders={false}
        >
          <Column field="employeeName" header="Colaborador" sortable />
          <Column
            field="totalHours"
            header="Total Horas"
            sortable
            style={{ width: "30%", textAlign: "right" }}
            body={(rowData) =>
              `${new Intl.NumberFormat("de-DE", {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              }).format(Number(rowData.totalHours))} h`
            }
          />
          <Column
            field="totalEuros"
            header="Total Euros"
            sortable
            style={{ width: "30%", textAlign: "right" }}
            body={(rowData) =>
              new Intl.NumberFormat("de-DE", {
                style: "currency",
                currency: "EUR",
              }).format(rowData.totalEuros)
            }
          />
        </DataTable>
      </div>
    );
  };

  const expandAll = () => {
    setExpandedRows(groupedData);
  };

  const collapseAll = () => {
    setExpandedRows([]);
  };

  const exportToExcel = () => {
    if (!groupedData || groupedData.length === 0) return;

    const exportData: any[] = [];

    groupedData.forEach((po) => {
      if (po.employees && po.employees.length > 0) {
        po.employees.forEach((emp: any) => {
          exportData.push({
            Cliente: po.clientName,
            "Nome da PO": po.poName,
            "Número da PO": po.poNumber,
            "Tipo da PO": po.poType,
            Colaborador: emp.employeeName,
            "Total Horas": emp.totalHours,
            "Total Euros": emp.totalEuros,
          });
        });
      } else {
        // Caso não tenha colaboradores, mas queremos ver a linha da PO
        exportData.push({
          Cliente: po.clientName,
          "Nome da PO": po.poName,
          "Número da PO": po.poNumber,
          "Tipo da PO": po.poType,
          Colaborador: "-",
          "Total Horas": po.totalHours,
          "Total Euros": po.totalEuros,
        });
      }
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Resumo PO");

    const monthStr = String(month).padStart(2, "0");
    XLSX.writeFile(workbook, `Resumo_Mensal_PO_${year}_${monthStr}.xlsx`);
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "1rem",
          marginBottom: "1.5rem",
          marginTop: "1rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <span style={{ fontWeight: "600", color: "var(--text-color)" }}>
            Mês de Referência:
          </span>
          <Calendar
            value={selectedDate}
            onChange={(e) => {
              if (e.value instanceof Date) {
                setSelectedDate(e.value);
              }
            }}
            view="month"
            dateFormat="mm/yy"
            showIcon
            style={{ width: "200px" }}
          />
        </div>

        <MultiSelect
          value={selectedClients}
          options={uniqueClients}
          onChange={(e) => setSelectedClients(e.value)}
          optionLabel="label"
          placeholder="Filtrar por Cliente"
          maxSelectedLabels={1}
          filter
          style={{ minWidth: "200px", maxWidth: "300px" }}
          emptyMessage="Nenhum Cliente disponível"
        />

        <MultiSelect
          value={selectedPOs}
          options={uniquePOs}
          onChange={(e) => setSelectedPOs(e.value)}
          optionLabel="label"
          placeholder="Filtrar por PO"
          maxSelectedLabels={1}
          filter
          style={{ minWidth: "200px", maxWidth: "300px" }}
          emptyMessage="Nenhuma PO disponível"
        />

        <MultiSelect
          value={selectedEmployees}
          options={uniqueEmployees}
          onChange={(e) => setSelectedEmployees(e.value)}
          optionLabel="label"
          placeholder="Filtrar por Colaborador"
          maxSelectedLabels={1}
          filter
          style={{ minWidth: "200px", maxWidth: "300px" }}
          emptyMessage="Nenhum Colaborador disponível"
        />

        <div style={{ display: "flex", gap: "0.5rem", marginLeft: "auto" }}>
          <Button
            icon="pi pi-plus"
            onClick={expandAll}
            className="p-button-text p-button-sm"
          />
          <Button
            icon="pi pi-minus"
            onClick={collapseAll}
            className="p-button-text p-button-sm"
          />
          <Button
            icon="pi pi-file-excel"
            onClick={exportToExcel}
            className="p-button-success p-button-sm"
            title="Exportar dados achatados para Excel"
          />
        </div>
      </div>

      <DataTable
        value={groupedData}
        loading={isSummaryLoading}
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={rowExpansionTemplate}
        dataKey="poNumber"
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        emptyMessage="Nenhum registo realizado encontrado para o mês selecionado."
        style={{ marginTop: "1rem" }}
      >
        <Column expander style={{ width: "3rem" }} />
        <Column
          field="clientName"
          header="Cliente"
          sortable
          body={(rowData) => (
            <span style={{ fontWeight: "bold" }}>{rowData.clientName}</span>
          )}
        />
        <Column
          field="poName"
          header="PO"
          sortable
          body={(rowData) => (
            <span style={{ fontWeight: "bold" }}>
              {rowData.poName} ({rowData.poNumber})
            </span>
          )}
        />
        <Column
          field="poType"
          header="Tipo da PO"
          sortable
          body={(rowData) => (
            <span style={{ fontWeight: "bold" }}>{rowData.poType}</span>
          )}
        />
        <Column
          field="totalHours"
          header="Total Horas"
          sortable
          style={{ width: "15%", textAlign: "right" }}
          body={(rowData) => (
            <span style={{ fontWeight: "bold" }}>
              {new Intl.NumberFormat("de-DE", {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              }).format(Number(rowData.totalHours))}{" "}
              h
            </span>
          )}
        />
        <Column
          field="totalEuros"
          header="Total Euros"
          sortable
          style={{ width: "15%", textAlign: "right" }}
          body={(rowData) => (
            <span style={{ fontWeight: "bold" }}>
              {new Intl.NumberFormat("de-DE", {
                style: "currency",
                currency: "EUR",
              }).format(rowData.totalEuros)}
            </span>
          )}
        />
      </DataTable>
    </>
  );
};

export default PurchaseOrderMonthlySummary;
